import { logClientError } from 'src/util/server-log';
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AddressServer } from "@models/address";
import { EnvironmentType, HOMOLOG_URL, PRODUCTION_URL } from "@models/env";
import { CreditDebitCard, PaymentMethodType } from "@models/payment";
import { User } from "@models/user";
import { CookieService } from "ngx-cookie-service";
import { Observable, of, ReplaySubject } from "rxjs";
import { environment } from "src/environments/environment";
import * as SubscriptionProto from "@proto/subscriptions/subscriptions";

@Injectable({
  providedIn: "root",
})
export class SharedService {
  user?: User | null;
  public user$ = new ReplaySubject<User | null>(1);

  private _refreshToken: string | null = null;
  private _accessToken: string | null = null;

  // Mapeamento dos status de subscription conforme o enum Status do proto:
  // STATUS_UNSPECIFIED = 0, STATUS_CREATED = 1, STATUS_ACTIVE = 2, STATUS_PAST_DUE = 3,
  // STATUS_CANCELED = 4, STATUS_TRIALING = 5, STATUS_PAUSED = 6, STATUS_EXPIRED = 7, STATUS_PENDING = 8
  private readonly subscriptionStatusMap: { [key: number]: { text: string; bgColor: string; textColor: string } } = {
    0: { text: "Status não especificado", bgColor: "bg-gray-100", textColor: "text-gray-600" },
    1: { text: "Criada", bgColor: "bg-blue-50", textColor: "text-blue-700" },
    2: { text: "Ativa", bgColor: "bg-green-50", textColor: "text-green-700" },
    3: { text: "Pagamento atrasado", bgColor: "bg-red-50", textColor: "text-red-700" },
    4: { text: "Cancelada", bgColor: "bg-red-50", textColor: "text-red-700" },
    5: { text: "Período de teste", bgColor: "bg-purple-50", textColor: "text-purple-700" },
    6: { text: "Pausada", bgColor: "bg-orange-50", textColor: "text-orange-700" },
    7: { text: "Expirada", bgColor: "bg-gray-100", textColor: "text-gray-700" },
    8: { text: "Pendente", bgColor: "bg-yellow-50", textColor: "text-yellow-700" },
  };

  private _currentPlan: any | null = null;
  public plan$ = new ReplaySubject<any | null>(1);

  private _tempNewPlan: any | null = null;

  // Stores the plan ID when user needs to add a card before completing upgrade
  pendingUpgradePlanId: string | null = null;

  // Stores the checkout session ID for upgrade flow
  upgradeCheckoutId: string | null = null;
  upgradeRegistrationCompleted = false;
  upgradeTermsAccepted = false;
  upgradeContractTermsUrl = "";
  retentionCheckout: { checkoutId: string; planId: string; campaignId: string } | null = null;

  private _currentSubscription: any | null = null;
  public currentSubscription$ = new ReplaySubject<any | null>(1);

  private _activeSubscription: any | null = null;
  public activeSubscription$ = new ReplaySubject<any | null>(1);

  private _subscriptions: any[] = [];
  public subscriptions$ = new ReplaySubject<any[]>(1);

  private _activePlan: any | null = null;
  public activePlan$ = new ReplaySubject<any | null>(1);

  private _sessionId: string | null = null;
  public sessionId$ = new ReplaySubject<string | null>(1);

  publicKey: string | null = null;
  private _wallet: any | null = null;
  public wallet$ = new ReplaySubject<any | null>(1);

  // Default payment method from getDefaultPayment (pix or creditCard)
  private _defaultPayment: any | null = null;
  public defaultPayment$ = new ReplaySubject<any | null>(1);

  public isInitialDataLoaded = false;
  public planId: string | null = null;

  registerForm: User | null = null;
  changeCard$: ReplaySubject<any> = new ReplaySubject<any>(1);
  mfaChallenge: {
    mfaSessionId?: string;
    mfaMethods?: string[];
  };
  pendingPasswordChange: string | null = null;
  checkout: {
    sessionId: string | null;
    planId?: string | null;
    document: string;
    stateRegistration: string;
    companyName: string;
    birthDate: string;
    address: AddressServer | null;
    selectedPaymentMethod: PaymentMethodType | null;
    card: CreditDebitCard | null;
    couponCode?: string | null;
    discountAppliedAmount?: number;
    originalAmount: number | null;
    finalAmount: number | null;
  } = {
    sessionId: null,
    planId: null,
    document: "",
    stateRegistration: "",
    companyName: "",
    birthDate: "",
    address: null,
    selectedPaymentMethod: null,
    card: null,
    couponCode: null,
    discountAppliedAmount: 0,
    originalAmount: null,
    finalAmount: null,
  };

  updateCheckout(data: Partial<SharedService["checkout"]>) {
    this.checkout = { ...this.checkout, ...data };
  }

  clearCheckout() {
    this.checkout = {
      sessionId: null,
      planId: null,
      document: "",
      stateRegistration: "",
      companyName: "",
      birthDate: "",
      address: null,
      selectedPaymentMethod: null,
      card: null,
      couponCode: null,
      discountAppliedAmount: 0,
      originalAmount: null,
      finalAmount: null,
    };
    this.sessionId = null;
  }

  get wallet(): any | null {
    return this._wallet;
  }

  set wallet(value: any | null) {
    this._wallet = value;
    this.wallet$.next(value);
  }

  get defaultPayment(): any | null {
    return this._defaultPayment;
  }

  set defaultPayment(value: any | null) {
    this._defaultPayment = value;
    this.defaultPayment$.next(value);
  }

  private readonly COOKIE_EXPIRY_DAYS = 30;

  private getCookieExpiry(): Date {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + this.COOKIE_EXPIRY_DAYS);
    return expiryDate;
  }

  constructor(
    public cookieService: CookieService,
    public router: Router,
  ) {
    this.user$.subscribe((user) => {
      this.user = user;
      this.cookieService.set("user", JSON.stringify(user) ?? "", { path: "/", expires: this.getCookieExpiry() });
    });
    this.accessToken = this.cookieService.get("accessToken");
    this.refreshToken = this.cookieService.get("refreshToken");
  }

  updateCookies(): void {
    const expires = this.getCookieExpiry();
    if (this.accessToken) {
      this.cookieService.set("accessToken", this.accessToken, { path: "/", expires });
    }
    if (this.refreshToken) {
      this.cookieService.set("refreshToken", this.refreshToken, { path: "/", expires });
    }
    if (this.user) {
      this.cookieService.set("user", JSON.stringify(this.user), { path: "/", expires });
    }
  }

  get accessToken(): string | null {
    return this._accessToken;
  }

  get refreshToken(): string | null {
    return this._refreshToken;
  }

  set accessToken(value: string | null) {
    this._accessToken = value;
    if (value) {
      this.cookieService.set("accessToken", value, { path: "/", expires: this.getCookieExpiry() });
    } else {
      this.cookieService.delete("accessToken", "/");
    }
  }

  set refreshToken(value: string | null) {
    this._refreshToken = value;
    if (value) {
      this.cookieService.set("refreshToken", value, { path: "/", expires: this.getCookieExpiry() });
    } else {
      this.cookieService.delete("refreshToken", "/");
    }
  }

  get currentPlan(): any | null {
    return this._currentPlan;
  }

  set currentPlan(value: any | null) {
    this._currentPlan = value;
    this.plan$.next(value);
  }

  get tempNewPlan(): any | null {
    return this._tempNewPlan;
  }

  set tempNewPlan(value: any | null) {
    this.upgradeRegistrationCompleted = false;
    this.upgradeTermsAccepted = false;
    this.upgradeContractTermsUrl = "";
    this.upgradeCheckoutId = null;
    this._tempNewPlan = value;
  }

  get currentSubscription(): ReturnType<
    typeof SubscriptionProto.subscriptions.v1.Subscription.prototype.toObject
  > | null {
    return this._currentSubscription;
  }

  set currentSubscription(value: any | null) {
    this._currentSubscription = value;
    this.currentSubscription$.next(value);
  }

  get activeSubscription(): ReturnType<
    typeof SubscriptionProto.subscriptions.v1.Subscription.prototype.toObject
  > | null {
    return this._activeSubscription;
  }

  set activeSubscription(value: any | null) {
    this._activeSubscription = value;
    this.activeSubscription$.next(value);
  }

  get subscriptions(): any[] {
    return this._subscriptions;
  }

  set subscriptions(value: any[]) {
    this._subscriptions = value;
    this.subscriptions$.next(value);
  }

  get activePlan(): any | null {
    return this._activePlan;
  }

  set activePlan(value: any | null) {
    this._activePlan = value;
    this.activePlan$.next(value);
  }

  get sessionId(): string | null {
    return this._sessionId;
  }

  set sessionId(value: string | null) {
    this._sessionId = value;
    this.sessionId$.next(value);
  }

  loadDataWithUser(user?: User): Observable<boolean> {
    this.user = user;
    this.user$.next(user ?? null);
    return of(user ? true : false);
  }

  setRedirectUrl(url: string | null): void {
    if (url === null || url === undefined) {
      this.cookieService.delete("redirectUrl", "/");
    } else {
      this.cookieService.set("redirectUrl", url, { path: "/" });
    }
  }

  getRedirectUrl(): string | null {
    const url = this.cookieService.get("redirectUrl");
    // Return null if the cookie is empty, "null", or "undefined"
    if (!url || url === "null" || url === "undefined") {
      return null;
    }
    return url;
  }

  getPlanId(): string | null {
    if (!this.planId) {
      this.planId = localStorage.getItem("planId");
    }
    return this.planId;
  }

  setPlanId(planId: string | null): void {
    this.planId = planId;
    if (planId) {
      localStorage.setItem("planId", planId);
    } else {
      localStorage.removeItem("planId");
    }
  }

  continueReading(): void {
    const persistentUrl = this.getRedirectUrl();
    this.setRedirectUrl(null);
    if (persistentUrl && persistentUrl.trim() !== "") {
      window.location.href = persistentUrl;
    } else {
      window.location.href = environment.env === EnvironmentType.homolog ? HOMOLOG_URL : PRODUCTION_URL;
    }
  }

  logoutUser(navigate: boolean = true): void {
    // Limpa tokens
    this.accessToken = null;
    this.refreshToken = null;

    // Limpa usuário
    this.user = null;
    this.user$.next(null);

    // Limpa subscription e planos
    this.currentSubscription = null;
    this.activeSubscription = null;
    this.subscriptions = [];
    this.currentPlan = null;
    this.activePlan = null;
    this.tempNewPlan = null;
    this.pendingUpgradePlanId = null;
    this.upgradeCheckoutId = null;
    this.retentionCheckout = null;

    // Limpa wallet e pagamentos
    this.wallet = null;
    this.defaultPayment = null;

    // Limpa session
    this.sessionId = null;
    this.isInitialDataLoaded = false;

    // Limpa checkout
    this.clearCheckout();

    // Limpa planId
    this.setPlanId(null);

    // Limpa dados de formulário e MFA
    this.registerForm = null;
    this.mfaChallenge = { mfaSessionId: undefined, mfaMethods: undefined };
    this.pendingPasswordChange = null;

    // Limpa cookies
    this.cookieService.delete("accessToken", "/");
    this.cookieService.delete("refreshToken", "/");
    this.cookieService.delete("user", "/");
    this.cookieService.delete("redirectUrl", "/");

    // Limpa localStorage - todos os dados relacionados à sessão do usuário
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("idToken");
    localStorage.removeItem("pixPaymentData");
    localStorage.removeItem("onboarding_completed");
    localStorage.removeItem("pending_payment_update");
    localStorage.removeItem("oauth_state");
    localStorage.removeItem("code_verifier");

    // Limpa sessionStorage
    sessionStorage.removeItem("oauth_state");
    sessionStorage.removeItem("pendingRedirectUrl");

    // Navega para login se solicitado
    if (navigate) {
      this.router.navigateByUrl("/entrar");
    }
  }

  getSubscriptionStatusText(status: number): string {
    return this.subscriptionStatusMap[status]?.text || this.subscriptionStatusMap[0].text;
  }

  getSubscriptionStatusStyles(status: number): { bgColor: string; textColor: string } {
    return this.subscriptionStatusMap[status] || this.subscriptionStatusMap[0];
  }

  /**
   * Checks if the current plan (from planId URL param) is free (price = 0).
   * This is used to skip checkout flow for free plans.
   * @returns true if the plan exists and has price.amount === 0
   */
  isCurrentPlanFree(): boolean {
    if (!this._currentPlan) {
      return false;
    }
    return this._currentPlan.price?.amount === 0;
  }

  /**
   * Redirects to SVM_HOME and clears the planId.
   * Used when a free plan is selected - skips checkout and goes directly to SVM_HOME.
   * Always redirects to SVM_HOME (not redirectUrl) because user may have come from paywall/vitrine.
   */
  redirectToOriginAndClearPlan(): void {
    this.setPlanId(null);
    this.setRedirectUrl(null);
    window.location.href = environment.SVM_HOME;
  }

  /**
   * Refreshes the current subscription and plan from the server.
   * This method should be called after any subscription change (upgrade/downgrade/cancel).
   * It updates both the local properties and emits new values through the ReplaySubjects.
   * @param grpcSubscriptionService - The gRPC subscription service to fetch data
   * @returns Promise that resolves when both subscription and plan are updated
   */
  async refreshSubscriptionAndPlan(grpcSubscriptionService: {
    getCurrentSubscription: () => Promise<any>;
    getPlanById: (id: string) => Promise<any>;
    getActiveSubscription: () => Promise<any>;
  }): Promise<void> {
    try {
      // Fetch updated current subscription
      const subscriptionResponse = await grpcSubscriptionService.getCurrentSubscription();
      const subscription = subscriptionResponse.toObject().subscription;
      this.currentSubscription = subscription;
      // Fetch updated plan if subscription has a plan
      if (subscription?.plan?.id) {
        const planResponse = await grpcSubscriptionService.getPlanById(subscription.plan.id);
        const plan = (planResponse as any).toObject().plan;
        this.currentPlan = plan;
      } else {
        this.currentPlan = null;
      }

      // Fetch active subscription (STATUS_ACTIVE = 2)
      await this.refreshActiveSubscription(grpcSubscriptionService);
    } catch (error) {
      logClientError("[SharedService] Error refreshing subscription and plan:", error);
      throw error;
    }
  }

  /**
   * Busca a subscription ativa do usuário (status = ACTIVE).
   * Diferente de currentSubscription que é a última subscription (pode estar pendente).
   * @param grpcSubscriptionService - O serviço gRPC para buscar os dados
   * @returns Promise que resolve quando a activeSubscription é atualizada
   */
  async refreshActiveSubscription(grpcSubscriptionService: {
    getActiveSubscription: () => Promise<any>;
    getPlanById: (id: string) => Promise<any>;
  }): Promise<void> {
    try {
      const response = await grpcSubscriptionService.getActiveSubscription();
      const activeSubscription = response.toObject().subscription || null;

      this.activeSubscription = activeSubscription;
      // Fetch plan for active subscription
      if (activeSubscription?.plan?.id) {
        const planResponse = await grpcSubscriptionService.getPlanById(activeSubscription.plan.id);
        const plan = (planResponse as any).toObject().plan;
        this.activePlan = plan;
      } else {
        this.activePlan = null;
      }
    } catch (error) {
      logClientError("[SharedService] Error fetching active subscription:", error);
      // Em caso de erro, não quebra o fluxo - apenas loga
    }
  }

  /**
   * Encontra a subscription ativa (STATUS_ACTIVE = 2) em uma lista de subscriptions.
   * @param subscriptions - Lista de subscriptions do usuário
   * @returns A subscription com status ACTIVE ou null se não encontrar
   */
  findActiveSubscription(
    subscriptions: ReturnType<typeof SubscriptionProto.subscriptions.v1.Subscription.prototype.toObject>[],
  ): ReturnType<typeof SubscriptionProto.subscriptions.v1.Subscription.prototype.toObject> | null {
    // STATUS_ACTIVE = 2
    const STATUS_ACTIVE = 2;
    return subscriptions.find((sub) => sub.status === STATUS_ACTIVE) || null;
  }

  /**
   * Stores a redirect URL in sessionStorage for after the phone modal completes.
   */
  setPendingRedirectUrl(url: string | null): void {
    if (url) {
      sessionStorage.setItem("pendingRedirectUrl", url);
    } else {
      sessionStorage.removeItem("pendingRedirectUrl");
    }
  }

  /**
   * Checks whether the current user is missing a phone number.
   * Returns true if the user exists but has no phone.
   */
  isUserMissingPhone(): boolean {
    if (!this.user) return false;
    const phone = this.user.phone?.replace(/\D/g, "") || "";
    return phone.length === 0;
  }
}
